import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GetJson, PutJson } from '@awp/helpers/fetchReact';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { REST_API, NULL_OPTION, FORM_MODE, Permissions } from '@awp/constants';
import * as Yup from 'yup';

import {
  getAvalancheGeographicAreas,
  selectGeographicAreaLinesDd,
  selectGeographicAreaPointsDd,
} from '@awp/stores/avalancheSlice';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import { selectPhasedEventTypes } from '@awp/stores/eventSlice';

import { selectAvalancheTechnicians } from '@awp/stores/dropdownsSlice';
import { Card, Spinner, Button, Container, Row } from 'react-bootstrap';

import { FormTextArea, FormSelect, FormDatePicker, FormTextReadOnly } from '@components/Common/AwpFormInputs';
import { hasAccess } from '@components/Authorize';
import { Formik, Form } from 'formik';
import { convLocalToIsoString, getUtcToLocal } from '@awp/helpers/utilsReact';
import { toast } from 'react-toastify';
import AwpYesNoModal from '@components/Common/AwpYesNoModal';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import { useAvalancheEventContext } from './AvalanceEventContext';
import ProcessedCriticalFieldsEdit from './ProcessedCriticalFieldsEdit';
import {
  criticalFieldsToEntity,
  checkEventType,
  findEventTypeNmById,
  isCriticalFieldsUpdated,
} from './ProcessedCriticalFieldsHelper';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';

const ProcessedPhasedEdit = ({
  setMode,
  mode,
  setEventId,
  eventId,
  setLoading,
  loading,
  fullData,
  setEventTypeToActOn,
  breadcrumbName,
}) => {
  const { fullContextData } = useAvalancheEventContext() ?? {};

  const loadedData = fullContextData ?? fullData;

  const currentUser = useSelector(selectCurrentUser);
  const location = useLocation();
  const navigate = useNavigate();
  const avalanchePrograms = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));
  const avalancheTechs = useSelector((state) => selectAvalancheTechnicians(state));
  const areaLines = useSelector((state) => selectGeographicAreaLinesDd(state));
  const areaPoints = useSelector((state) => selectGeographicAreaPointsDd(state));
  const phasedEventTypes = useSelector(selectPhasedEventTypes);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const defaultDto = {
    eventType: null,
    program: null,
    area: null,
    segment: null,
    fromPoint: null,
    toPoint: null,
    roadClosedTime: new Date(),
    timestamp: new Date(),
    comment: '',
    entityStatus: '',
    concurrencyControlNumber: null,
  };

  const [dto, setDto] = useState(defaultDto);
  const [eventErrors, setEventErrors] = useState(null);
  const [isAvalancheTech, setIsAvalancheTech] = useState(true);
  const [areaList, setAreaList] = useState(avalancheAreas);
  const [lineList, setLineList] = useState(areaLines);
  const [pointList, setPointList] = useState(areaPoints);
  const [originalValues, setOriginalValues] = useState(null);
  const [showCriticalFieldsModal, setShowCriticalFieldsModal] = useState(false);
  const [showCriticalFields, setShowCriticalFields] = useState(false);
  const [criticalFields, setCriticalFields] = useState(null);
  const [loadingCriticalField, setLoadingCriticalField] = useState(false);
  const [eventPaths, setEventPaths] = useState(null);
  const [eventSegment, setEventSegment] = useState(null);
  const [eventFrom, setEventFrom] = useState(null);
  const [eventTo, setEventTo] = useState(null);
  const nullOption = NULL_OPTION;

  const readOnly = mode !== FORM_MODE.EDIT && mode !== FORM_MODE.CREATE;
  const buttonLabel = 'Save';

  const title = mode === FORM_MODE.EDIT ? 'Edit Processed Phased Event' : 'Processed Phased Event';

  useEffect(() => {
    if (mode === FORM_MODE.CREATE || mode === FORM_MODE.EDIT) {
      setIsAvalancheTech(avalancheTechs.options.findIndex((t) => t.value === currentUser.SecurityIdentifierId) !== -1);
    }

    setLoading(true);

    const url = REST_API.PROCESSED_PHASED + eventId;

    GetJson(url)
      .then((response) => response.json())
      .then((entity) => {
        let _dto = entityToDto(entity);
        setDto(_dto);
        setOriginalValues(_dto);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const programOnChange = (opt, setFieldValue) => {
    setAreaList(avalancheAreas.filter((x) => x.parentId === opt.value));
    setLineList(areaLines.filter((x) => x.programId === opt.value));
    setPointList(areaPoints.filter((x) => x.programId === opt.value));
    setFieldValue('area', nullOption, false);
    setFieldValue('segment', nullOption, false);
    setFieldValue('fromPoint', nullOption, false);
    setFieldValue('toPoint', nullOption, false);
    //setDto({ ...dto, area: nullOption, segment: nullOption, fromPoint: nullOption, toPoint: nullOption });
  };

  const areaOnChange = (opt, setFieldValue) => {
    setPointList(areaPoints.filter((x) => x.areaId === opt.value));
    setLineList(areaLines.filter((x) => x.areaId === opt.value));
    setFieldValue('segment', nullOption, false);
    setFieldValue('fromPoint', nullOption, false);
    setFieldValue('toPoint', nullOption, false);
    resetEventPaths();
  };

  const segmentOnChange = (opt, setFieldValue, lines, points) => {
    setFieldValue('fromPoint', nullOption, false);
    setFieldValue('toPoint', nullOption, false);
  };

  const pointOnChange = (opt, setFieldValue) => {
    setFieldValue('segment', nullOption, false);
  };

  const entityToDto = (entity) => {
    const area = avalancheAreas.find((x) => x.value === entity.GeographicAreaId);

    const dtoTemp = {
      eventType: phasedEventTypes.find((x) => x.value === entity.EventType.Name),
      program: avalanchePrograms.find((x) => x.value === area.parentId),
      area: area,
      segment:
        entity.ParentEvent && entity.ParentEvent.GeographicLineId
          ? areaLines.find((x) => x.value === entity.ParentEvent.GeographicLineId)
          : nullOption,
      fromPoint:
        entity.ParentEvent && entity.ParentEvent.RoadClosurePoints && entity.ParentEvent.RoadClosurePoints.length === 2
          ? areaPoints.find((x) => x.value === entity.ParentEvent.RoadClosurePoints[0].GeographicPointId)
          : nullOption,
      toPoint:
        entity.ParentEvent && entity.ParentEvent.RoadClosurePoints && entity.ParentEvent.RoadClosurePoints.length === 2
          ? areaPoints.find((x) => x.value === entity.ParentEvent.RoadClosurePoints[1].GeographicPointId)
          : nullOption,
      roadClosedTime: new Date(getUtcToLocal(entity.ParentEvent.Event.EventTimestamp)),
      timestamp: new Date(getUtcToLocal(entity.EventTimestamp)),
      comment: entity.NoteComment,
      entityStatus: entity.EntityStatusType.StatusValue,
      concurrencyControlNumber: entity.ConcurrencyControlNumber,
      eventId: entity.EventId,
      lastUpdateUserId: entity.LastUpdateUserId ? (entity.LastUpdateUserId + '').toUpperCase() : '',
      lastUpdatetimestamp: entity.LastUpdatetimestamp ? getUtcToLocal(entity.LastUpdatetimestamp + '') : '',
      createUserId: entity.CreateUserId ? (entity.CreateUserId + '').toUpperCase() : '',
    };

    dtoTemp.program && setAreaList(avalancheAreas.filter((x) => x.parentId === dtoTemp.program.value));
    dtoTemp.area && setLineList(areaLines.filter((x) => x.areaId === dtoTemp.area.value));
    dtoTemp.area && setPointList(areaPoints.filter((x) => x.areaId === dtoTemp.area.value));

    return dtoTemp;
  };

  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    if (originalValues && values) {
      if (isCriticalFieldsUpdated(criticalFields, values, originalValues)) {
        setShowCriticalFieldsModal(true);
        setSubmitting(true);
        setSubmitting(false);
        return;
      }
    }
    setSubmitting(true);
    const entity = {
      eventId: eventId,
      eventTypeName: values.eventType?.value,
      timestamp: convLocalToIsoString(values.timestamp ?? new Date()),
      comment: values.comment,
      concurrencyControlNumber: dto.concurrencyControlNumber,
    };
    if (criticalFields) {
      entity.criticalFields = criticalFieldsToEntity(criticalFields, values);
    } else {
      entity.criticalFields = null;
    }
    const url = REST_API.PROCESSED_PHASED + eventId;

    PutJson(url, entity)
      .then(async (response) => {
        if (response.ok) {
          toast.success('Processed phased event has been updated');
          const entity = await response.json();
          const dtoTemp = entityToDto(entity);
          setDto(dtoTemp);
          setOriginalValues(dtoTemp);
          resetCriticalFields();
          resetForm();
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEventErrors({ Entity: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleBtnClose = (props) => {
    props.resetForm();
    if (mode === FORM_MODE.CREATE) {
      navigate(location.pathname);
    }
    setMode(FORM_MODE.LIST);
  };
  const handleCriticalSection = (props) => {
    setShowCriticalFieldsModal(false);
    setLoadingCriticalField(true);

    const url = REST_API.PROCESSED_LINKED_EVENTS + eventId;

    GetJson(url)
      .then((response) => response.json())
      .then((entity) => {
        const _entity = entity;
        if (_entity) {
          setCriticalFields(_entity);
          getEventPaths(_entity);
          setShowCriticalFields(true);
        }
      })
      .finally(() => {
        setLoadingCriticalField(false);
      });
  };
  const resetEventPaths = () => {
    if (!eventPaths) return;
    if (Array.isArray(eventPaths)) {
      let _eventPaths = [];
      eventPaths.map((c) => {
        _eventPaths.push({ eventId: c.EventId, value: null });
      });
      setEventPaths(_eventPaths);
      return;
    }
  };
  const checkEventPaths = () => {
    if (!eventPaths) return false;
    if (Array.isArray(eventPaths)) {
      const ep = eventPaths.find((c) => {
        return !c.value;
      });
      if (ep) return false;
    }
    return true;
  };
  const getEventPathValue = (id) => {
    if (Array.isArray(eventPaths)) {
      const ep = eventPaths.find((c) => {
        return c.eventId === id;
      });
      if (ep) return ep.value;
    }
    return null;
  };
  const updateEventPath = (id, v) => {
    let _eventPaths = [];
    let found = false;
    if (Array.isArray(eventPaths)) {
      eventPaths.map((c) => {
        if (c.eventId === id) {
          _eventPaths.push({ eventId: id, value: { ...v } });
          found = true;
        } else {
          _eventPaths.push(c);
        }
      });
      if (!found) _eventPaths.push({ eventId: id, value: v });
      if (_eventPaths && Array.isArray(_eventPaths) && _eventPaths.length > 0) setEventPaths(_eventPaths);
      return;
    }
  };
  const getEventPaths = (entity) => {
    if (!entity) return;
    let _eventPaths = [];
    let _children = entity.ChildAvalancheProcessedEvents;
    if (Array.isArray(_children)) {
      _children.map((c) => {
        let _gChildren = c.ChildAvalancheProcessedEvents;
        if (Array.isArray(_gChildren)) {
          _gChildren.map((gc) => {
            if (checkEventType(gc.EventTypeNm)) {
              _eventPaths.push({ eventId: gc.EventId, value: null });
            }
          });
        }
        if (checkEventType(c.EventTypeNm)) {
          _eventPaths.push({ eventId: c.EventId, value: null });
        }
      });
    }
    if (checkEventType(entity.EventTypeNm)) {
      _eventPaths.push({ eventId: entity.EventId, value: null });
    }
    if (_eventPaths && Array.isArray(_eventPaths) && _eventPaths.length > 0) setEventPaths(_eventPaths);
  };
  // true = failed /false = passed
  const validCriticalFields = (values) => {
    if (!criticalFields || !dto) return false;
    const evtType = findEventTypeNmById(criticalFields, dto.eventId);
    if (evtType && !checkEventType(evtType)) {
      if (!criticalFields.ChildAvalancheProcessedEvents || criticalFields.ChildAvalancheProcessedEvents.length === 0)
        return false;
      if (!eventPaths) return false;
      return !checkEventPaths();
    }
    return false;
  };

  const resetCriticalFields = () => {
    setCriticalFields(null);
    setEventPaths(null);
    setEventSegment(null);
    setEventFrom(null);
    setEventTo(null);
  };
  const breadCrumb = () => {
    const entity = loadedData?.find((x) => x.id === eventId);
    const name = entity?.internal.avalanche_program?.label;
    return name ? name : eventId;
  };

  const Buttons = ({ props }) => {
    return mode === FORM_MODE.EDIT ? (
      <>
        <div className="mt-2 mr-3" style={{ float: 'left' }}>
          {!showCancelConfirm && (
            <Button
              className="mr-2"
              variant="outline-primary"
              onClick={() => {
                setShowCancelConfirm(true);
              }}
            >
              {'Cancel'}
            </Button>
          )}

          {!showCancelConfirm && isAvalancheTech && hasAccess([Permissions.WriteAvalancheForm]) && (
            <Button
              type="submit	"
              variant="primary"
              disabled={!props.isValid || !props.dirty || props.isSubmitting || validCriticalFields(props.values)}
            >
              {props.isSubmitting ? (
                <>
                  <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                <span>{buttonLabel}</span>
              )}
            </Button>
          )}
          {showCancelConfirm && (
            <AwpYesNoDialog
              action={() => handleBtnClose(props)}
              setShowYesNoConfirm={setShowCancelConfirm}
              msg={'Are you sure you want to cancel?'}
              props={{}}
            />
          )}
        </div>
      </>
    ) : (
      <Button
        className="mr-2"
        variant="outline-primary"
        style={{ marginTop: '40px' }}
        onClick={() => {
          handleBtnClose(props);
        }}
      >
        {'Close'}
      </Button>
    );
  };

  Buttons.propTypes = {
    title: PropTypes.string,
    props: PropTypes.any,
    resetForm: PropTypes.func,
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    dirty: PropTypes.bool,
  };

  return (
    <>
      {loading ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : (
        <>
          <span className="awp-text-btn" onClick={() => setMode(FORM_MODE.LIST)}>
            {breadcrumbName}
          </span>
          <span className="mx-3">&gt;</span>
          <span>{breadCrumb()}</span>
          <Formik
            enableReinitialize
            validateOnMount
            validateOnBlur
            validateOnChange
            initialValues={dto}
            validationSchema={Yup.object({
              area: Yup.object()
                .nullable()
                .test('location', 'Avalanche Area is required', (value, context) => {
                  if (context.parent.area && context.parent.area.value) return true;
                  return false;
                }),
              segment: Yup.object()
                .nullable()
                .test('location', 'Either Road Closure Area or From/To is required', (value, context) => {
                  if (
                    (context.parent.segment && context.parent.segment.value) ||
                    (context.parent.fromPoint && context.parent.fromPoint.value) ||
                    (context.parent.toPoint && context.parent.toPoint.value)
                  )
                    return true;
                  return false;
                }),
              fromPoint: Yup.object()
                .nullable()
                .test('location', 'Either Road Closure Area or From/To is required', (value, context) => {
                  if (
                    (context.parent.segment && context.parent.segment.value) ||
                    ((!context.parent.segment || !context.parent.segment.value) &&
                      context.parent.fromPoint &&
                      context.parent.fromPoint.value)
                  )
                    return true;
                  return false;
                }),
              toPoint: Yup.object()
                .nullable()
                .test('location', 'Either Road Closure Area or From/To is required', (value, context) => {
                  if (
                    (context.parent.segment && context.parent.segment.value) ||
                    ((!context.parent.segment || !context.parent.segment.value) &&
                      context.parent.toPoint &&
                      context.parent.toPoint.value)
                  )
                    return true;
                  return null;
                }),
              comment: Yup.string().optional().label('Comment').max(500, '${label} must not exceed 500 characters'),
              eventType: Yup.object().nullable().required('Phased Event type is required'),
              timestamp: Yup.date().nullable().required('Road Closure Date is required'),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <>
                <div className="d-flex">
                  <h1 className="flex-grow-1">{title}</h1>
                </div>
                <Form>
                  <Card
                    border="light"
                    className="page-card"
                    style={{ border: '0px', boxShadow: '0px 0px 0px #000000' }}
                  >
                    <Card.Body>
                      {(mode === FORM_MODE.EDIT || mode === FORM_MODE.VIEW) && dto && (
                        <>
                          <FormTextReadOnly label="Created By" textValue={dto.createUserId} />
                          <FormTextReadOnly label="Last Updated" textValue={dto.lastUpdatetimestamp} />
                          <FormTextReadOnly label="Last Updated By" textValue={dto.lastUpdateUserId} />
                        </>
                      )}
                      <FormSelect
                        className="avalanche-select"
                        readOnly={readOnly}
                        name="program"
                        id="program"
                        label="Program"
                        options={avalanchePrograms}
                        postChange={(e) => {
                          programOnChange(e, props.setFieldValue);
                        }}
                        isMulti={false}
                      />

                      <FormSelect
                        className="avalanche-select"
                        readOnly={readOnly}
                        name="area"
                        id="area"
                        label="Avalanche Area"
                        options={areaList}
                        isMulti={false}
                        postChange={(e) => {
                          areaOnChange(e, props.setFieldValue);
                        }}
                      />
                      {dto.segment && (
                        <FormSelect
                          className="avalanche-select"
                          readOnly={readOnly}
                          name="segment"
                          id="segment"
                          label="Road Closure Area"
                          options={lineList}
                          isMulti={false}
                          postChange={(e) => {
                            segmentOnChange(e, props.setFieldValue);
                          }}
                        />
                      )}

                      {dto.fromPoint && (
                        <FormSelect
                          className="avalanche-select"
                          readOnly={readOnly}
                          name="fromPoint"
                          id="fromPoint"
                          label="From"
                          options={pointList.filter((x) => x.value !== props.values.toPoint?.value)}
                          isMulti={false}
                          postChange={(e) => {
                            pointOnChange(e, props.setFieldValue);
                          }}
                        />
                      )}

                      {dto.toPoint && (
                        <FormSelect
                          className="avalanche-select"
                          readOnly={readOnly}
                          name="toPoint"
                          id="toPoint"
                          label="To"
                          options={pointList.filter((x) => x.value !== props.values.fromPoint?.value)}
                          isMulti={false}
                          postChange={(e) => {
                            pointOnChange(e, props.setFieldValue);
                          }}
                        />
                      )}
                      <div className="customWidthDatePicker">
                        <FormDatePicker
                          readOnly={true}
                          label="Road Closed"
                          id="roadClosedTime"
                          name="roadClosedTime"
                          showTimeSelect={true}
                          showTime={{ isEnabled: true }}
                          timeFormat="HH:mm"
                          timeIntervals={30}
                          dateFormat={['yyyy-MM-dd HH:mm']}
                        />
                      </div>
                      <FormSelect
                        className="avalanche-select"
                        readOnly={readOnly}
                        name="eventType"
                        id="eventType"
                        label="Phased Event"
                        options={phasedEventTypes}
                        isMulti={false}
                        isDisabled={true}
                      />
                      <div className="customWidthDatePicker">
                        <FormDatePicker
                          readOnly={readOnly}
                          label="Date/Time"
                          id="timestamp"
                          name="timestamp"
                          showTimeSelect={true}
                          showTime={{ isEnabled: true }}
                          timeFormat="HH:mm"
                          timeIntervals={30}
                          dateFormat={['yyyy-MM-dd HH:mm']}
                        />
                      </div>
                      <FormTextArea readOnly={readOnly} label="Comment" id="comment" name="comment" rows={3} />
                      <div>
                        <b>Form Status:</b> <span>{dto.entityStatus}</span>
                      </div>
                      <Buttons title=" " props={props} />
                      {loadingCriticalField && (
                        <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                      )}
                      {!loadingCriticalField &&
                        !showCriticalFieldsModal &&
                        showCriticalFields &&
                        eventId > 0 &&
                        criticalFields && (
                          <div style={{ display: 'block', clear: 'both', paddingTop: '1rem' }}>
                            <ProcessedCriticalFieldsEdit
                              dto={criticalFields}
                              setCriticalFields={setCriticalFields}
                              criticalFields={criticalFields}
                              updatedDto={props.values}
                              eventPaths={eventPaths}
                              updateEventPath={updateEventPath}
                              getEventPathValue={getEventPathValue}
                              eventSegment={eventSegment}
                              setEventSegment={setEventSegment}
                              eventFrom={eventFrom}
                              setEventFrom={setEventFrom}
                              eventTo={eventTo}
                              setEventTo={setEventTo}
                              isParent={true}
                            />
                            <Row style={{ height: '1rem' }} />
                          </div>
                        )}
                      {!loadingCriticalField &&
                        !showCriticalFieldsModal &&
                        showCriticalFields &&
                        eventId > 0 &&
                        criticalFields && (
                          <div style={{ display: 'block', clear: 'both', paddingTop: '1rem' }}>
                            {criticalFields &&
                              criticalFields.ChildAvalancheProcessedEvents &&
                              Object.keys(criticalFields.ChildAvalancheProcessedEvents).map((d, idx) => {
                                return (
                                  <>
                                    <ProcessedCriticalFieldsEdit
                                      key={idx}
                                      dto={criticalFields.ChildAvalancheProcessedEvents[d]}
                                      setCriticalFields={setCriticalFields}
                                      criticalFields={criticalFields}
                                      updatedDto={props.values}
                                      eventPaths={eventPaths}
                                      updateEventPath={updateEventPath}
                                      getEventPathValue={getEventPathValue}
                                      eventSegment={eventSegment}
                                      setEventSegment={setEventSegment}
                                      eventFrom={eventFrom}
                                      setEventFrom={setEventFrom}
                                      eventTo={eventTo}
                                      setEventTo={setEventTo}
                                      isParent={false}
                                    />
                                    <Row style={{ height: '1rem' }} />
                                  </>
                                );
                              })}
                          </div>
                        )}
                    </Card.Body>
                  </Card>
                </Form>
              </>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setEventErrors} errors={eventErrors} />
          <AwpYesNoModal
            showYesNoModal={showCriticalFieldsModal}
            setShowYesNoModal={setShowCriticalFieldsModal}
            onClickYes={handleCriticalSection}
            title="Warning"
            body="Changing the location information will result in the related elements needing to be set again. Are you sure you want to proceed?"
          />
        </>
      )}
    </>
  );
};

ProcessedPhasedEdit.propTypes = {
  setMode: PropTypes.func,
  mode: PropTypes.string,
  setEventId: PropTypes.func,
  eventId: PropTypes.number,
  setLoading: PropTypes.func,
  loading: PropTypes.bool,
  setFieldValue: PropTypes.func,
  validateField: PropTypes.func,
  setFieldTouched: PropTypes.func,
  values: PropTypes.any,
  resetForm: PropTypes.func,
  fullData: PropTypes.array,
  setEventTypeToActOn: PropTypes.func,
};

export default ProcessedPhasedEdit;
